import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { closeKlacht, KlachtError } from '../../lib/klachten';
import { useKlachtDialogStore } from '../../stores/klacht-dialog-store';

interface Props {
  onClosed?: () => void;
}

/**
 * Dialog voor handmatig sluiten van een klacht. Vereist een resolutie-tekst
 * die in `Klacht__c.Resolutie_Notitie__c` belandt.
 */
export function KlachtCloseDialog({ onClosed }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'close';
  const klachtId = payload.klachtId;

  const [resolutie, setResolutie] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setResolutie('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!klachtId || !resolutie.trim()) {
      setError('Resolutie is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await closeKlacht({ klachtId, resolutie: resolutie.trim() });
      close();
      onClosed?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Sluiten mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, resolutie, close, onClosed]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Klacht sluiten</h2>
          <p className="text-xs text-slate-500 mt-1">
            Sluit deze klacht definitief af. Je kan hem later heropenen.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">
              Resolutie <span className="text-red-600">*</span>
            </span>
            <textarea
              value={resolutie}
              onChange={(e) => setResolutie(e.target.value)}
              rows={4}
              placeholder="Korte beschrijving van wat er is afgesproken/uitgevoerd."
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !resolutie.trim()}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !resolutie.trim()
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-700 text-white hover:bg-slate-800'
            )}
          >
            {submitting ? 'Bezig…' : 'Klacht sluiten'}
          </button>
        </div>
      </div>
    </div>
  );
}
